import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SurveillanceCameraDto } from '@parking/shared';
import { api } from '../api/client';
import { AppModal } from './AppModal';
import { ParkingDataTable } from './ParkingDataTable';
import { StatusBadge } from './StatusBadge';

type FormState = {
  code: string;
  location: string;
  url: string;
  isActive: boolean;
};

const emptyForm = (): FormState => ({
  code: '',
  location: '',
  url: '',
  isActive: true,
});

type Props = {
  canWrite: boolean;
};

export function SettingsSurveillancePanel({ canWrite }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['settings-surveillance-cameras'],
    queryFn: () => api.listSurveillanceCameras(),
  });

  const rows = list.data?.data ?? [];

  const closeDialog = () => {
    setDialog(false);
    setEditingId(null);
    setFormError(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditingId(null);
    setFormError(null);
    setForm(emptyForm());
    setDialog(true);
  };

  const openEdit = (c: SurveillanceCameraDto) => {
    setEditingId(c.id);
    setFormError(null);
    setForm({
      code: c.code,
      location: c.location,
      url: c.url,
      isActive: c.isActive,
    });
    setDialog(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim(),
        location: form.location.trim(),
        url: form.url.trim(),
        isActive: form.isActive,
      };
      if (editingId) return api.updateSurveillanceCamera(editingId, payload);
      return api.createSurveillanceCamera(payload);
    },
    onSuccess: async () => {
      closeDialog();
      await qc.invalidateQueries({ queryKey: ['settings-surveillance-cameras'] });
      await qc.invalidateQueries({ queryKey: ['surveillance-cameras'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteSurveillanceCamera(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings-surveillance-cameras'] });
      await qc.invalidateQueries({ queryKey: ['surveillance-cameras'] });
    },
  });

  const columns = [
    t('settings.surveillance.code'),
    t('settings.surveillance.location'),
    t('settings.surveillance.url'),
    t('settings.surveillance.status'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted small mb-0">{t('settings.surveillance.hint')}</p>
        {canWrite && (
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
            {t('settings.surveillance.create')}
          </button>
        )}
      </div>

      {list.isError && <div className="alert alert-danger">{(list.error as Error).message}</div>}

      <ParkingDataTable
        storageKey="settings-surveillance"
        columns={columns}
        refreshKey={rows.map((c) => `${c.id}:${c.isActive}:${c.url}`).join('|')}
        actionsColumn={canWrite}
        galleryItems={rows.map((c) => ({
          id: c.id,
          icon: 'bi-broadcast',
          iconTone: c.isActive ? ('success' as const) : ('secondary' as const),
          title: c.code,
          subtitle: c.location,
          meta: c.url,
          badge: (
            <StatusBadge tone={c.isActive ? 'success' : 'secondary'}>
              {c.isActive ? t('common.activeOn') : t('common.activeOff')}
            </StatusBadge>
          ),
          actions: canWrite ? (
            <>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-primary"
                onClick={() => openEdit(c)}
              >
                <i className="bi bi-pencil" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-danger"
                onClick={() => {
                  if (window.confirm(t('settings.surveillance.confirmDelete'))) {
                    remove.mutate(c.id);
                  }
                }}
              >
                <i className="bi bi-trash" />
              </button>
            </>
          ) : undefined,
        }))}
      >
        {rows.map((c) => (
          <tr key={c.id}>
            <td className="fw-semibold">{c.code}</td>
            <td>{c.location}</td>
            <td className="text-break small">{c.url}</td>
            <td>
              <StatusBadge tone={c.isActive ? 'success' : 'secondary'}>
                {c.isActive ? t('common.activeOn') : t('common.activeOff')}
              </StatusBadge>
            </td>
            {canWrite && (
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  onClick={() => openEdit(c)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  onClick={() => {
                    if (window.confirm(t('settings.surveillance.confirmDelete'))) {
                      remove.mutate(c.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </td>
            )}
          </tr>
        ))}
      </ParkingDataTable>

      <AppModal
        open={dialog}
        onClose={closeDialog}
        title={editingId ? t('settings.surveillance.edit') : t('settings.surveillance.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeDialog}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                save.isPending || !form.code.trim() || !form.location.trim() || !form.url.trim()
              }
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('settings.surveillance.code')}</label>
            <input
              className="form-control"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.surveillance.location')}</label>
            <input
              className="form-control"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            />
          </div>
          <div className="modal-form__full">
            <label className="form-label">{t('settings.surveillance.url')}</label>
            <input
              className="form-control"
              type="url"
              placeholder="https://ibericam.com/player/modern/player.html?..."
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            />
            <div className="form-text">{t('settings.surveillance.urlHint')}</div>
          </div>
          <div className="modal-form__full">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="surv-active"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="surv-active">
                {t('settings.surveillance.isActive')}
              </label>
            </div>
          </div>
        </div>
      </AppModal>
    </>
  );
}
